import React, { useState } from "react";
import {
  Form,
  PageCaption,
  VersionCode,
} from "../FromSignIn/styles";
import {
  Alert,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Snackbar,
  Typography,
  Box,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Logo from "../../assets/logo-large.png";
import { changePassword } from "../../api/endpoints/auth";
import packageJs from "../../../package.json";

const ChangePassword = ({ userId, onSuccess, onCancel }) => {
  const [values, setValues] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    showCurrentPassword: false,
    showNewPassword: false,
    showConfirmPassword: false,
  });
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
    setError(false); // Limpar erro ao digitar
  };

  const handleClickShowPassword = (field) => () => {
    setValues({
      ...values,
      [field]: !values[field],
    });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  const validateForm = () => {
    if (!values.currentPassword) {
      setErrorMessage("Por favor, informe a senha atual!");
      setError(true);
      return false;
    }

    if (!values.newPassword) {
      setErrorMessage("Por favor, informe a nova senha!");
      setError(true);
      return false;
    }

    if (values.newPassword.length < 6) {
      setErrorMessage("A nova senha deve ter pelo menos 6 caracteres!");
      setError(true);
      return false;
    }

    if (values.newPassword !== values.confirmPassword) {
      setErrorMessage("As senhas não coincidem!");
      setError(true);
      return false;
    }

    if (values.currentPassword === values.newPassword) {
      setErrorMessage("A nova senha deve ser diferente da senha atual!");
      setError(true);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(false);

    try {
      const response = await changePassword(
        userId,
        values.currentPassword,
        values.newPassword
      );

      if (response && response.status) {
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setErrorMessage(
          response.message || "Erro ao alterar senha. Tente novamente."
        );
        setError(true);
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
          error.message ||
          "Erro ao alterar senha. Verifique se a senha atual está correta."
      );
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setError(false);
  };

  return (
    <Form>
      <img src={Logo} alt="Logo" />
      <PageCaption>Alterar Senha</PageCaption>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Por segurança, você precisa alterar sua senha antes de continuar.
        </Typography>
      </Box>

      <FormControl variant="outlined" fullWidth>
        <InputLabel htmlFor="current-password">Senha Atual</InputLabel>
        <OutlinedInput
          id="current-password"
          type={values.showCurrentPassword ? "text" : "password"}
          value={values.currentPassword}
          onChange={handleChange("currentPassword")}
          onKeyDown={handleKeyPress}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword("showCurrentPassword")}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {values.showCurrentPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
          label="Senha Atual"
        />
      </FormControl>

      <FormControl variant="outlined" fullWidth>
        <InputLabel htmlFor="new-password">Nova Senha</InputLabel>
        <OutlinedInput
          id="new-password"
          type={values.showNewPassword ? "text" : "password"}
          value={values.newPassword}
          onChange={handleChange("newPassword")}
          onKeyDown={handleKeyPress}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword("showNewPassword")}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {values.showNewPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
          label="Nova Senha"
        />
      </FormControl>

      <FormControl variant="outlined" fullWidth>
        <InputLabel htmlFor="confirm-password">Confirmar Nova Senha</InputLabel>
        <OutlinedInput
          id="confirm-password"
          type={values.showConfirmPassword ? "text" : "password"}
          value={values.confirmPassword}
          onChange={handleChange("confirmPassword")}
          onKeyDown={handleKeyPress}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword("showConfirmPassword")}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {values.showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
          label="Confirmar Nova Senha"
        />
      </FormControl>

      <VersionCode> Versão: {packageJs.version} </VersionCode>
      
      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={loading}
        fullWidth
        sx={{ mb: 1 }}
      >
        {loading ? "Alterando..." : "Alterar Senha"}
      </Button>

      {onCancel && (
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
          fullWidth
        >
          Cancelar
        </Button>
      )}

      <Snackbar
        open={error}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          '& .MuiSnackbar-root': {
            top: '24px !important',
          }
        }}
      >
        <Alert
          variant="filled"
          onClose={handleClose}
          severity="error"
          sx={{
            minWidth: '400px',
            maxWidth: '600px',
            width: 'auto',
            fontSize: '16px',
            padding: '16px 20px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            '& .MuiAlert-message': {
              padding: '0',
              fontWeight: 500,
              lineHeight: 1.5,
            },
            '& .MuiAlert-icon': {
              fontSize: '24px',
              marginRight: '12px',
            },
            '& .MuiAlert-action': {
              paddingLeft: '16px',
              '& .MuiIconButton-root': {
                padding: '4px',
              }
            }
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Form>
  );
};

export default ChangePassword;








